import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { STORAGE_CUSTOMER_DATA } from 'src/app/core/constants';
import { CartItem, MenuItemForPayment } from 'src/app/core/models/cart-item.model';
import {
  CatalogData,
  Merchant,
  Restaurant,
  RestaurantAvailableStatus,
  RestaurantProduct,
  Taxes,
} from 'src/app/core/models/restaurant.model';
import { OrderApiService } from 'src/app/core/services/order.api.service';
import { ProcessDataService } from 'src/app/core/services/process-data.service';
import { RouteService } from 'src/app/core/services/route.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { UtilService } from 'src/app/core/services/util.service';
import { CartStore } from 'src/app/core/stores/cart.store';
import { MerchantStore } from 'src/app/core/stores/merchant.store';
import { PaymentStore } from 'src/app/core/stores/payment.store';
import { RestaurantStore } from 'src/app/core/stores/restaurant.store';
import { Platform } from '@ionic/angular';
import { Platforms } from '../../../core/enums/util.enum';
import { InfoMessage } from '../../../core/enums/message.enum';
import { OrderStatus } from '../../../core/models/order.model';

@Component({
  selector: 'app-order-history',
  templateUrl: './order-history.component.html',
  styleUrls: ['./order-history.component.scss'],
})
export class OrderHistoryComponent implements OnInit, OnDestroy {
  public merchant = new Merchant();
  public previousOrders: OrderStatus[] = [];
  public userVerified = false;
  public title: string = InfoMessage.enter_phone;
  public hideLoadMore = false;
  public isSpinner: boolean;

  private locationId: string;
  private restaurant: Restaurant;
  private destroyed$ = new Subject<void>();
  private cartProduct: MenuItemForPayment;
  private isTaxes = false;
  private filteredProductList: RestaurantProduct[][];
  private filteredCategoriesIndexes = [];
  private currentProductId: string;
  private taxes: Taxes[];
  private isAlcoholHidden = false;
  private order: OrderStatus = new OrderStatus();
  private productUnavailable = false;
  private menuItemsInCart: CartItem[];
  private pageNumber: number = 0;
  private pageSize: number = 5;

  public get checkPlatform(): boolean {
    return this.platform.is(Platforms.CORDOVA);
  }

  constructor(
    private merchantStore: MerchantStore,
    private titleService: Title,
    public paymentStore: PaymentStore,
    private router: Router,
    private routeService: RouteService,
    private cartStore: CartStore,
    private orderApiService: OrderApiService,
    public restaurantStore: RestaurantStore,
    private utilService: UtilService,
    private storageService: StorageService,
    private processDataService: ProcessDataService,
    private platform: Platform,
  ) {
    this.cartStore.items$.subscribe(() => {
      this.menuItemsInCart = this.cartStore.cartItems;
    });
    this.restaurantStore.getIsRestaurantLoaded().subscribe(data => {
      if (data) {
        this.isSpinner = false;
        this.utilService.dismissActiveLoader();
      }
    });
  }

  ngOnInit() {
    this.isSpinner = true;
    this.restaurantStore.bootstrapRestaurants(this.routeService.getMerchantId());
    this.merchantStore.getLoyaltyProgram(this.routeService.getMerchantId());
    this.merchantStore.merchantData$.pipe(filter(Boolean), takeUntil(this.destroyed$)).subscribe((merchant: Merchant) => {
      this.merchant = merchant;
      if (this.merchant.merchantName) {
        this.titleService.setTitle(this.merchant.merchantName);
      }
    });
    this.getPreviousOrders();
  }
  ngOnDestroy() {
    this.destroyed$.next();
  }

  public async getPreviousOrders(): Promise<void> {
    this.isSpinner = true;
    await this.openLoadModalForCreek(InfoMessage.loading_history);
    const storageData = await this.storageService.get(STORAGE_CUSTOMER_DATA);
    if (storageData && storageData.orderBotUserId) {
      this.userVerified = true;
      this.paymentStore.verificationCustomer = true;
      const { orderBotUserId } = storageData;
      this.orderApiService.getPerviousOrders(orderBotUserId, this.pageNumber, this.pageSize)
        .subscribe((result: OrderStatus[]) => {
          this.isSpinner = false;
          this.utilService.dismissActiveLoader();
          if (result && result.length > 0) {
            this.pageNumber++;
            this.previousOrders = [...this.previousOrders, ...result];
            this.previousOrders.sort((a, b) => new Date(b.datetime_created).getTime() - new Date(a.datetime_created).getTime());
          } else {
            if (this.previousOrders && this.previousOrders.length > 0) {
            this.hideLoadMore = true;
            }
          }
        });
    } else {
      this.userVerified = false;
      this.isSpinner = false;
      await this.utilService.dismissActiveLoader();
    }
  }

  public async reOrder(order: any): Promise<void> {
    this.isSpinner = true;
    this.cartStore.clearCartAndOrderMap();
    await this.openLoadModalForCreek(InfoMessage.adding_products);
    this.order = order;
    this.locationId = order.locationId;
    this.getRestaurant();
  }

  private getRestaurant(): void {
    this.restaurantStore
      .getRestaurantById(this.locationId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (restaurant) => {
        if (restaurant) {
          this.restaurant = restaurant;
          this.processDataService.setRestaurantData(this.restaurant);
          this.isTaxes = false;
          this.getRestaurantAvailableStatus();
          this.restaurantStore.checkIsCatalogUploaded(restaurant.merchantId).subscribe(async (result: any) => {
            this.getRestaurantCatalogData(result.uploaded);
          });
        } else {
          this.isSpinner = false;
          await this.utilService.dismissActiveLoader();
          await this.utilService.presentAlert(
            InfoMessage.store_closed,
            InfoMessage.close_location
          );
        }
      }, error => {
        this.isSpinner = false;
      });
  }

  private getRestaurantCatalogData(isCatalogUploaded: boolean): any {
    this.restaurantStore
      .getRestaurantCatalogData(this.locationId, isCatalogUploaded, false, this.merchant.merchantId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        async (response: CatalogData) => {
          response.taxes.length === 1 && response.taxes[0].id === this.locationId ? (this.taxes = []) : (this.taxes = response.taxes);
          this.restaurantStore.categoryLoadedQueue = [...response.categories];
          this.restaurantStore.loadItemsInProgress = true;
          this.loadNextCategory(isCatalogUploaded, true, 0);
        },
        () => {
          if (isCatalogUploaded) {
            this.getRestaurantCatalogData(false);
          }
        }
      );
  }

  private loadNextCategory(isCatalogUploaded: boolean, isEmptyProductsList: boolean, categoryIndex: number): void {
    if (this.restaurantStore.categoryLoadedQueue.length !== 0) {
      this.restaurantStore
        .getProductsByCategoryId(this.locationId, isCatalogUploaded, isEmptyProductsList, this.restaurantStore.categoryLoadedQueue[0])
        .subscribe(
          async (products: RestaurantProduct[]) => {
            this.isTaxes = true;
            this.restaurantStore.categoryLoadedQueue.shift();
            this.filterProducts(products, isEmptyProductsList, categoryIndex);
            const allVariationIds = this.restaurantStore.getCategoryVariationIds(products);
            const variationIdsWithInventory = this.restaurantStore.getUseInventoryItems(products);
            if (this.restaurant.enforceInventory && allVariationIds.length > 0) {
              this.processDataService.loadNextVariationsInventory(this.locationId, allVariationIds);
            } else if (variationIdsWithInventory.length > 0) {
              this.processDataService.loadNextVariationsInventory(this.locationId, variationIdsWithInventory);
            }
            this.loadNextCategory(isCatalogUploaded, false, categoryIndex + 1);
          },
          () => {
            if (isCatalogUploaded) {
              this.loadNextCategory(false, false, categoryIndex);
            }
          }
        );
    } else {
      this.createOrder();
      this.restaurantStore.loadItemsInProgress = false;
    }
  }

  private async createOrder(): Promise<any> {
    const merchantId = this.routeService.getMerchantId();
    this.order.lineItems.forEach((item) => {
      this.initProduct(item, this.order);
      this.cartStore.updateCartInProgress = true;
      this.cartStore.updateCartInProgress = false;
    });
    await this.utilService.dismissActiveLoader();
    if (!this.restaurant.isAcceptingOrders || !this.restaurant.isMobileEnabled) {
      await this.utilService.presentAlert(InfoMessage.store_closed, InfoMessage.close_location);
    } else {
      if (this.productUnavailable || this.menuItemsInCart.length === 0) {
        await this.utilService.presentAlert(
          InfoMessage.product_unavailable,
          InfoMessage.any_product_unavailable,
          false,
          merchantId,
          this.order.locationId
        );
      } else {
        await this.router.navigate(['merchants', merchantId, 'locations', this.order.locationId, 'checkout']);
      }
    }
  }

  private getRestaurantAvailableStatus(): void {
    this.restaurantStore.restaurantAvailableStatus$
      .pipe(filter(Boolean), takeUntil(this.destroyed$))
      .subscribe((restaurantAvailableStatus: RestaurantAvailableStatus) => {
        const { isCurbsideEnabled, isTablesideEnabled, isPickupEnabled, isDeliveryEnabled, isXSideEnabled } = restaurantAvailableStatus;
        this.restaurantStore.getRestaurantFulfillment(this.restaurant, this.restaurantStore.fulfillmentParams);

        // If restricting fulfillment using query params, don't allow orders if matching fulfillment disabled
        if (
          this.restaurantStore.fulfillmentParams != null
          && this.restaurantStore.fulfillmentParams.length > 0
          && this.restaurant.isMobileEnabled
        ) {
          const fulfillmentStatus = {
            pickup: isPickupEnabled,
            curbside: isCurbsideEnabled,
            tableside: isTablesideEnabled,
            delivery: isDeliveryEnabled,
            xside: isXSideEnabled,
          };
          const activeFulfillments = this.restaurantStore.fulfillmentParams.filter((param) => fulfillmentStatus[param]);
          this.restaurant.isMobileEnabled = activeFulfillments.length > 0;
        }
      });
  }

  private filterProducts(products: RestaurantProduct[], isEmptyProductsList: boolean, categoryIndex: number): void {
    if (isEmptyProductsList) {
      this.filteredCategoriesIndexes = [];
      this.filteredProductList = [];
    }
    const newCategory: any = [];
    for (const item of products) {
      let showItem = true;
      if (
        (this.restaurant && // Hide unorderable time-restricted items during service for ASAP orders
          this.restaurant.isAcceptingOrders &&
          !this.restaurant.allowScheduledPickups &&
          !this.restaurant.showAllTimeRestrictedItems &&
          !this.validateItemAvailableTimes(item)) || // Hide items with incorrect fulfillment types
        (!this.restaurant.showAllFulfillmentRestrictedItems && !this.validateItemAvailableFulfillments(item))
      ) {
        showItem = false;
      }
      if (showItem) {
        newCategory.push(item);
      }
    }
    if (newCategory.length > 0) {
      this.filteredCategoriesIndexes.push(categoryIndex);
      if (
        this.isAlcoholHidden &&
        this.restaurant &&
        this.restaurant.allowScheduledPickups &&
        this.restaurantStore.filterItemsInCategory(newCategory, this.isAlcoholHidden).length > 0
      ) {
        this.filteredProductList.push(this.restaurantStore.filterItemsInCategory(newCategory, this.isAlcoholHidden));
      } else {
        this.filteredProductList.push(newCategory);
      }
    }
  }

  private validateItemAvailableTimes(item: RestaurantProduct): boolean {
    const isValidLeadTime = this.processDataService.validateItemLeadTime(item);
    const isValidWeekDay = this.processDataService.validateItemAvailableDays(item);
    return isValidLeadTime && isValidWeekDay;
  }

  private validateItemAvailableFulfillments(item: RestaurantProduct): boolean {
    return this.processDataService.validateItemAvailableFulfillments(item);
  }

  private initProduct(product: CartItem, order: OrderStatus): void {
    this.processDataService.specialInstructions = product.specialInstructions;
    this.processDataService.productQuantity = product.quantity;
    this.processDataService.modifiers = product.modifiers;
    this.currentProductId = product.variation.id;
    this.filteredProductList.forEach((products) => {
      products
        .filter((product) => product.locationId === order.locationId)
        .filter((product) =>
          product.variations.find((variation) => {
            if (variation.id === this.currentProductId) {
              this.addItemToCart(product);
            }
          })
        );
    });
  }

  private addItemToCart(product: any): void {
    const validateItem = this.processDataService.addItemToCart(product, this.currentProductId);
    if (validateItem && validateItem.length > 1 && validateItem[0]) {
      this.cartProduct = validateItem[1];
      this.cartStore.addItemToCart(this.cartProduct);
      this.cartStore.updateCartInProgress = true;
      const order = this.orderApiService.createOrder(this.cartProduct.locationId);
      if (!this.cartStore.orderMap[this.cartProduct.locationId]) {
        this.processDataService.sendOrder(this.cartProduct.locationId, order , this.taxes);
      } else {
        this.processDataService.calculateOrder(this.cartProduct.locationId, order);
      }
    } else {
      this.productUnavailable = true;
    }
  }

  public viewReceipt(url: string): void {
    this.utilService.openInAppBrowser(url);
  }

  public getRestaurantBrandColor(locationId: string): void {
    this.restaurantStore
      .getRestaurantById(locationId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((restaurant) => {
        if (restaurant) {
          const cards: any = document.getElementsByTagName('ion-card');
          for (const card of cards) {
            if (card.getAttribute('class')?.includes(locationId)) {
              card.style.setProperty('--background', restaurant.brandColor);
              card.style.setProperty('border', `2px solid ${restaurant.brandColor}`);
            }
          }
        }
      });
  }
  private async openLoadModalForCreek(modalText: string): Promise<void> {
    if (this.checkPlatform) {
      await this.utilService.activeLoader(modalText);
    }
  }
}
